import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Printer,
  Share2,
  X,
  Package,
  Loader2,
  FileText,
  ListChecks,
  Check,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Resolve which price a product gets shown at based on the chosen tier.
// Falls back to the base A+ price if the higher tier wasn't configured.
const tierPrice = (product, tier) => {
  if (tier === "B_plus") {
    const wp = Number(product.wholesale_price || 0);
    if (wp > 0) return wp;
  } else if (tier === "C_plus") {
    const cp = Number(product.c_plus_price || 0);
    if (cp > 0) return cp;
  }
  return Number(product.price || 0);
};

// ====== Step 1: Picker — multi-select products (sticky modal) ======
const ProductPickerModal = ({ products, picked, tier, onClose, onDone }) => {
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState({ ...picked });

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return products;
    return products.filter((p) =>
      (p.name_ar || "").toLowerCase().includes(t) ||
      (p.category || "").toLowerCase().includes(t)
    );
  }, [products, q]);

  const toggle = (p) => {
    setDraft((d) => {
      const next = { ...d };
      if (next[p.id]) {
        delete next[p.id];
      } else {
        next[p.id] = p;
      }
      return next;
    });
  };

  const selectAll = () => {
    const next = { ...draft };
    filtered.forEach((p) => { next[p.id] = p; });
    setDraft(next);
    toast.success(`تم اختيار ${filtered.length} منتج`);
  };

  const clearAll = () => {
    setDraft({});
    toast.message("تمّ مسح الاختيارات");
  };

  const pickedCount = Object.keys(draft).length;

  return (
    <div className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="price-picker-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 shrink-0 flex justify-between items-start">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2"><ListChecks className="w-5 h-5" /> اختيار منتجات للقائمة</h3>
            <p className="text-[10px] opacity-90 mt-0.5">اضغط على منتج لإضافته. النافذة تبقى مفتوحة حتى تنتهي.</p>
          </div>
          <button onClick={onClose} data-testid="price-picker-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-3 space-y-2 overflow-y-auto flex-1">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="price-picker-search"
              placeholder="ابحث عن منتج..."
              className="w-full pr-9 pl-4 py-2 rounded-full border-2 border-[#3B82F6]"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={selectAll} data-testid="price-picker-select-all" className="bg-[#7ED321] hover:bg-[#6cb91d] text-white text-xs font-bold py-1.5 px-3 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" /> اختيار الكل ({filtered.length})
            </button>
            <button onClick={clearAll} data-testid="price-picker-clear" className="bg-[#E85D45] hover:bg-[#d54d35] text-white text-xs font-bold py-1.5 px-3 rounded-full flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> مسح الاختيارات
            </button>
          </div>

          {filtered.length === 0 ? (
            <p className="text-center py-6 text-[#5C6E64] text-sm">لا توجد نتائج</p>
          ) : (
            filtered.map((p) => {
              const isPicked = !!draft[p.id];
              return (
                <button
                  key={p.id}
                  onClick={() => toggle(p)}
                  data-testid={`price-picker-row-${p.id}`}
                  className={`w-full rounded-xl p-2 flex items-center gap-2 border-2 transition-colors text-right ${
                    isPicked ? "bg-[#ECFDF5] border-[#7ED321]" : "bg-white border-[#E4EBE6] hover:border-[#3B82F6]"
                  }`}
                >
                  {p.image_path ? (
                    <img src={`${API}/files/${p.image_path}`} className="w-12 h-12 rounded-lg object-cover" alt="" />
                  ) : <div className="w-12 h-12 bg-[#F3F6F4] rounded-lg flex items-center justify-center"><Package className="w-4 h-4 text-[#5C6E64]" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold truncate flex items-center gap-1">
                      {isPicked && <span className="text-[#15803d]">✓</span>}
                      {p.name_ar}
                    </p>
                    <p className="text-[10px] text-[#5C6E64]">
                      السعر: <span className="font-black text-[#15803d]" dir="ltr">{fmt(tierPrice(p, tier))} د.ع</span>
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="bg-white border-t-2 border-[#E4EBE6] p-3 flex items-center justify-between gap-2 shrink-0">
          <div className="text-xs">
            <p className="text-[#5C6E64]">مُختار</p>
            <p data-testid="price-picker-count" className="font-black text-lg text-[#1E3A5F]" dir="ltr">{pickedCount}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="price-picker-cancel" className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] text-xs font-bold py-2 px-3 rounded-full">
              إلغاء
            </button>
            <button
              onClick={() => onDone(draft)}
              disabled={pickedCount === 0}
              data-testid="price-picker-attach"
              className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:bg-[#BAE6FD] text-white text-xs font-bold py-2 px-4 rounded-full flex items-center gap-1"
            >
              <Check className="w-3.5 h-3.5" /> إرفاق القائمة ({pickedCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ====== Step 2: Printable price list ======
const PriceListTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [picked, setPicked] = useState({}); // {id: product}
  const [customerName, setCustomerName] = useState("");
  const [showImages, setShowImages] = useState(true);
  // NEW (Feb 28, 2026): tier selector. Manager picks A+ / B+ / C+ and
  // the price column auto-swaps to that tier's price for every selected
  // product (falls back to A+ if the higher tier isn't configured on a
  // particular item).
  const [tier, setTier] = useState("A_plus");
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/products`);
        setProducts((data || []).filter((p) => Number(p.price) > 0));
      } catch { toast.error("فشل التحميل"); }
      finally { setLoading(false); }
    })();
  }, []);

  const items = useMemo(() => Object.values(picked), [picked]);

  const removeItem = (id) => {
    setPicked((p) => {
      const n = { ...p }; delete n[id]; return n;
    });
  };

  const handlePrint = () => {
    const node = printRef.current;
    if (!node) return;
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>قائمة الأسعار - جملتي</title>
<style>
@page { size: A4; margin: 12mm }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-family: 'Tajawal', system-ui, sans-serif; margin: 0; padding: 16px; color: #111; direction: rtl; }
h1 { color: #1E3A5F; margin: 0 0 4px; font-size: 22px; }
.sub { color: #5C6E64; font-size: 11px; margin-bottom: 12px; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #E4EBE6; padding: 8px; text-align: right; font-size: 13px; }
th { background: #1E3A5F; color: white; }
.thumb { width: 56px; height: 56px; object-fit: cover; border-radius: 8px; }
.price { color: #15803d; font-weight: 800; }
.footer { margin-top: 18px; font-size: 11px; color: #5C6E64; text-align: center; }
</style></head><body>${node.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const buildShareText = () => {
    const lines = [];
    lines.push("📋 قائمة أسعار — جملتي");
    const tierLabel = tier === "A_plus" ? "A+" : tier === "B_plus" ? "B+" : "C+";
    lines.push(`فئة الأسعار: ${tierLabel}`);
    if (customerName.trim()) lines.push(`إلى: ${customerName.trim()}`);
    lines.push(`التاريخ: ${new Date().toLocaleDateString("en-GB")}`);
    lines.push("─".repeat(20));
    items.forEach((p, i) => {
      lines.push(`${i + 1}. ${p.name_ar}\n   السعر: ${fmt(tierPrice(p, tier))} د.ع`);
    });
    lines.push("─".repeat(20));
    lines.push(`عدد الأصناف: ${items.length}`);
    lines.push(`https://jumlaty.online`);
    return lines.join("\n");
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(buildShareText());
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  if (loading) {
    return <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="price-list-tab" dir="rtl">
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-5 h-5" />
          <h3 className="font-bold text-lg">قائمة الأسعار</h3>
        </div>
        <p className="text-xs opacity-90">اختر المنتجات التي تريد إرسالها للزبون، ثم اطبع أو شاركها على واتساب.</p>
      </div>

      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          data-testid="price-customer-name"
          placeholder="اسم الزبون (اختياري — يظهر على الفاتورة)"
          className="px-3 py-2 rounded-xl border-2 border-[#3B82F6]"
        />
        <label className="flex items-center gap-2 bg-[#F8FAFC] rounded-xl px-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showImages}
            onChange={(e) => setShowImages(e.target.checked)}
            data-testid="price-show-images"
            className="w-4 h-4 accent-[#3B82F6]"
          />
          <ImageIcon className="w-4 h-4 text-[#5C6E64]" />
          <span className="text-sm">عرض صور المنتجات في القائمة</span>
        </label>
      </div>

      {/* Tier selector — swaps prices live across the picker preview and
          the printed/shared list. A+ = base price, B+ = wholesale_price,
          C+ = c_plus_price (each falls back to A+ if not configured). */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]">
        <p className="text-xs font-bold text-[#5B6B62] mb-2">فئة الأسعار المعروضة:</p>
        <div className="flex flex-wrap gap-2">
          {[
            { id: "A_plus", label: "A+ (سعر مفرد)", bg: "#15803D" },
            { id: "B_plus", label: "B+ (سعر جملة)", bg: "#2563EB" },
            { id: "C_plus", label: "C+ (سعر وسط)", bg: "#E85D45" },
          ].map((t) => {
            const active = tier === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTier(t.id)}
                data-testid={`price-tier-${t.id}`}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${active ? "text-white shadow-md scale-105" : "bg-[#F0F9FF] text-[#2D4A3E] hover:bg-[#E0F2FE]"}`}
                style={active ? { background: t.bg } : {}}
              >
                {active ? "✓ " : ""}{t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setShowPicker(true)}
          data-testid="price-open-picker"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
        >
          <ListChecks className="w-4 h-4" /> اختيار منتجات
        </button>
        <button
          onClick={handlePrint}
          disabled={items.length === 0}
          data-testid="price-print-btn"
          className="bg-[#1E3A5F] hover:bg-[#152a45] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
        >
          <Printer className="w-4 h-4" /> طباعة / تصدير PDF
        </button>
        <button
          onClick={handleWhatsApp}
          disabled={items.length === 0}
          data-testid="price-whatsapp-btn"
          className="bg-[#25D366] hover:bg-[#1ebe57] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
        >
          <Share2 className="w-4 h-4" /> مشاركة على واتساب
        </button>
      </div>

      {/* Preview area — this is what gets printed */}
      <div className="bg-white rounded-2xl p-4 border-2 border-[#E4EBE6]" ref={printRef} data-testid="price-preview">
        <h1>📋 قائمة أسعار — جملتي</h1>
        <div className="sub">
          {customerName.trim() && (<>إلى: <b>{customerName.trim()}</b> · </>)}
          التاريخ: <span dir="ltr">{new Date().toLocaleDateString("en-GB")}</span>
          {" · "} فئة الأسعار: <b dir="ltr">{tier === "A_plus" ? "A+" : tier === "B_plus" ? "B+" : "C+"}</b>
          {" · "} عدد الأصناف: <span dir="ltr">{items.length}</span>
        </div>
        {items.length === 0 ? (
          <p className="text-center text-[#5C6E64] text-sm py-8">لم يتم اختيار منتجات بعد. اضغط "اختيار منتجات".</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                {showImages && <th style={{ width: 72 }}>الصورة</th>}
                <th>اسم المنتج</th>
                <th style={{ width: 130 }}>السعر</th>
                <th style={{ width: 50 }} className="print:hidden">حذف</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={p.id} data-testid={`price-row-${p.id}`}>
                  <td dir="ltr" className="text-center">{i + 1}</td>
                  {showImages && (
                    <td className="text-center">
                      {p.image_path ? (
                        <img src={`${API}/files/${p.image_path}`} alt="" className="thumb" />
                      ) : <span style={{ color: "#5C6E64" }}>—</span>}
                    </td>
                  )}
                  <td>{p.name_ar}</td>
                  <td className="price" dir="ltr">{fmt(tierPrice(p, tier))} د.ع</td>
                  <td className="print:hidden text-center">
                    <button onClick={() => removeItem(p.id)} className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {items.length > 0 && (
          <div className="footer">شكراً لتعاملكم مع جملتي · jumlaty.online</div>
        )}
      </div>

      {showPicker && (
        <ProductPickerModal
          products={products}
          picked={picked}
          tier={tier}
          onClose={() => setShowPicker(false)}
          onDone={(d) => { setPicked(d); setShowPicker(false); toast.success(`تم إرفاق ${Object.keys(d).length} منتج`); }}
        />
      )}
    </div>
  );
};

export default PriceListTab;
